import { qnaQuestionSchema, type QnaQuestion } from "@engage/contracts";
import { RealtimeRpcError, type RealtimeConnection } from "@engage/realtime/client";
import { useState } from "preact/hooks";
import { useI18n } from "./i18n";

/**
 * Asking and voting. The list shows only questions the room may see — the server
 * never sends this phone a pending or rejected one — most wanted first.
 */
export function QnaView({
  connection,
  qna,
  onVoted,
}: {
  connection: RealtimeConnection;
  qna: { enabled: boolean; questions: QnaQuestion[]; myVotes: string[] };
  onVoted: (q: QnaQuestion) => void;
}) {
  const { t, plural } = useI18n();
  const [body, setBody] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | null>(null);

  if (!qna.enabled) return <p class="card muted">{t("qna.off")}</p>;

  const ask = async (e: Event) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    setNotice(null);
    try {
      const q = qnaQuestionSchema.parse(await connection.rpc("qna.ask", { body: body.trim(), isAnonymous: anonymous }));
      setBody("");
      setNotice({ text: q.status === "pending" ? t("qna.sentModerated") : t("qna.sent") });
    } catch (err) {
      const tooMany = err instanceof RealtimeRpcError && err.code === 4429;
      setNotice({ text: tooMany ? t("qna.tooMany") : t("join.error"), error: true });
    } finally {
      setBusy(false);
    }
  };

  const vote = async (q: QnaQuestion) => {
    try {
      onVoted(qnaQuestionSchema.parse(await connection.rpc("qna.upvote", { questionId: q.questionId })));
    } catch {
      /* already voted, or the question was just hidden: the list will catch up */
    }
  };

  const sorted = [...qna.questions].sort(
    (a, b) =>
      Number(a.status === "answered") - Number(b.status === "answered") ||
      b.upvoteCount - a.upvoteCount ||
      a.createdAt.localeCompare(b.createdAt),
  );

  return (
    <section class="stack">
      <form class="card stack" onSubmit={ask}>
        <h1>{t("qna.title")}</h1>
        <textarea
          value={body}
          maxLength={500}
          rows={3}
          placeholder={t("qna.placeholder")}
          onInput={(e) => setBody((e.target as HTMLTextAreaElement).value)}
        />
        <label class="check">
          <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous((e.target as HTMLInputElement).checked)} />
          {t("qna.anonymous")}
        </label>
        {notice && (
          <p class={notice.error ? "error" : "success"} role="status">
            {notice.text}
          </p>
        )}
        <button class="primary" type="submit" disabled={busy || !body.trim()}>
          {busy ? t("qna.asking") : t("qna.ask")}
        </button>
      </form>

      {sorted.length === 0 ? (
        <p class="muted center">{t("qna.empty")}</p>
      ) : (
        <ul class="questions">
          {sorted.map((q) => {
            const voted = qna.myVotes.includes(q.questionId);
            return (
              <li key={q.questionId} class={`card question${q.status === "answered" ? " answered" : ""}`}>
                <div>
                  <p>{q.body}</p>
                  <small class="muted">
                    {q.authorName ?? t("qna.anonymousAuthor")}
                    {q.status === "answered" && ` · ${t("qna.answered")}`}
                  </small>
                </div>
                <button
                  class={`vote${voted ? " voted" : ""}`}
                  disabled={voted || q.status === "answered"}
                  onClick={() => vote(q)}
                  aria-label={`${voted ? t("qna.upvoted") : t("qna.upvote")} (${plural("qna.votes", q.upvoteCount)})`}
                >
                  ▲ {q.upvoteCount}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
